<template>
  <BackgroundModal class="login__background">
    <template #title>
      <div v-if="!authStore.invitation">Log in to your account</div>
      <div v-if="authStore.invitation">
        Log in as {{ authStore.invitation.userFirstName }} to join
        {{ authStore.invitation.teamName }}
      </div>
    </template>
    <template #default>
      <form
        novalidate
        @submit.prevent="onLogin"
      >
        <GoogleAuthButton class="login__form__button" />

        <DarwinButton
          class="login__form__button"
          flair="rounded"
          kind="outline"
          size="lg"
          :to="{ name: 'LoginSSO' }"
          :disabled="loading"
        >
          <template #icon-prepend>
            <IconDuotoneKey />
          </template>
          Use single sign-on
        </DarwinButton>

        <Separator class="separator"> or </Separator>

        <div class="login__form__input">
          <InputField
            v-model="email"
            :error="error.email"
            :readonly="!!authStore.invitation"
            type="email"
            name="email"
            label="Email"
            autocomplete="email"
            required
            auto-focus
          />
        </div>

        <router-link
          class="login__form__input--forgot__password"
          to="/forgot-password"
        >
          Forgot Password
        </router-link>

        <div class="login__form__input">
          <PasswordInput
            v-model="password"
            :error="error.password"
            autocomplete="current-password"
          />
        </div>

        <div class="login__form__remember">
          <CheckBox
            id="remember"
            v-model="remember"
            name="remember"
            size="md"
          >
            <template #label> Remember me </template>
          </CheckBox>
        </div>

        <DarwinButton
          class="login__form__button"
          flair="rounded"
          kind="primary"
          size="lg"
          type="submit"
          :disabled="loading"
        >
          Login
          <template #icon-shortcut>
            <Hotkey
              inverted
              size="md"
            >
              ↵
            </Hotkey>
          </template>
        </DarwinButton>
      </form>
    </template>
  </BackgroundModal>
</template>

<script lang="ts" setup>
import isEmail from 'validator/lib/isEmail'
import isEmpty from 'validator/lib/isEmpty'
import { computed, ref, watch, onMounted, onBeforeMount } from 'vue'
import type { Ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import IconDuotoneKey from '@/assets/icons/IconDuotoneKey.svg?inline'
import GoogleAuthButton from '@/modules/Auth/GoogleAuthButton.vue'
import BackgroundModal from '@/components/Common/BackgroundModal.vue'
import InputField from '@/components/Common/InputField/V2/InputField.vue'
import PasswordInput from '@/components/Common/PasswordInput/PasswordInput.vue'
import Separator from '@/components/Common/Separator.vue'

import { useToast } from '@/uiKit/Toast/useToast'
import { useAuthStore } from '@/modules/Auth/useAuthStore'
import type { Login2FAResponsePayload } from '@/store/types/Login2FAResponsePayload'
import DarwinButton from '@/uiKit/Button/DarwinButton.vue'
import CheckBox from '@/uiKit/Checkbox/CheckBox.vue'
import Hotkey from '@/uiKit/Hotkey/Hotkey.vue'
import type { ParsedError } from '@/backend/error'
import { getRefreshToken } from '@/backend/token'

import { useAttemptedRoute } from './useAttemptedRoute'

type LogInError = {
  email?: string
  password?: string
}

const authStore = useAuthStore()
const route = useRoute()
const router = useRouter()

const email = ref('')
const password = ref('')
const remember = ref(false)
const loading = ref(false)
const error: Ref<LogInError> = ref({})

const toast = useToast()

const handleLoginError = (response: ParsedError): void => {
  const { error: errorResponse } = response
  const { status, message } = errorResponse

  if (status === 410) {
    router.push({ name: 'AccountDeleted' })
    return
  }

  error.value = {}

  if (typeof message !== 'string') {
    toast.error({
      meta: { title: 'An unexpected error has ocurred' },
      duration: 3000,
    })
    return
  }

  if (status === 400) {
    error.value = { email: message }
  } else if (status === 401) {
    password.value = ''
    toast.error({
      meta: { title: message },
      duration: 3000,
    })
  } else {
    toast.error({
      meta: { title: message },
      duration: 3000,
    })
  }
}

const validateForm = (): boolean => {
  error.value = {}

  if (isEmpty(email.value)) {
    error.value.email = 'Email cannot be empty'
  }

  if (!isEmail(email.value)) {
    error.value.email = 'Should be a valid email'
  }

  if (isEmpty(password.value)) {
    error.value.password = 'Password cannot be empty'
  }

  return Object.keys(error.value).length === 0
}

const { redirectOnLogin } = useAttemptedRoute()

const setup2fa = async (): Promise<void> => {
  const result = await authStore.setup2fa()

  if ('error' in result) {
    toast.error({
      meta: { title: result.error.message.toString() },
      duration: 3000,
    })
    return
  }

  router.push({ name: 'SetupTfa', query: { secret_key: result.data.secret_2fa } })
}

const handleRequired2FA = async (response: Login2FAResponsePayload): Promise<void> => {
  const { required_2fa: required2fa, set_up_2fa: isSetup2fa } = response
  if (!required2fa) {
    await redirectOnLogin(router)
    return
  }

  authStore.SET_2FA_CREDENTIALS({
    email: email.value,
    password: password.value,
  })
  if (isSetup2fa) {
    router.push({ name: 'LoginTfa', query: { origin: 'login' } })
  } else {
    setup2fa()
  }
}

const onLogin = async (): Promise<void> => {
  if (!validateForm()) {
    return
  }

  const params = {
    email: email.value,
    password: password.value,
    rememberMe: remember.value,
  }

  loading.value = true

  const response = await authStore.login(params)

  loading.value = false

  if ('error' in response) {
    handleLoginError(response)
    return
  }

  if ('required_2fa' in response.data) {
    await handleRequired2FA(response.data)
    return
  }

  error.value = {}
  await redirectOnLogin(router)
}

onBeforeMount(async () => {
  // router.beforeEach will send us to this route, this component,
  // if the route we tried to access requires authentication
  // Upon tab open, we will also dispatch a request to any potential other
  // tabs that are open to send us the refresh token
  // This is because, if we logged in with "remember me" unchecked, the
  // refresh token is in session storage, so not available for a newly open tab
  // If we got here and in the meantime, got the refresh token, this will
  // log us in automatically
  if (!getRefreshToken()) {
    return
  }

  loading.value = true
  const user = await authStore.loginWithToken()
  loading.value = false

  if ('data' in user) {
    await redirectOnLogin(router)
  }
})

onMounted(() => {
  loading.value = false

  // check if fulfilling invitation
  if (authStore.invitation) {
    /**
     * In case of invitations, fill in the email with the one from the invitation
     */
    email.value = authStore.invitation.email
  }
})

const errorMessage = computed(() => route.query.error_message as string)
/**
 * Show error if route query contains a "error_message" key
 */
watch(
  errorMessage,
  (message) => {
    if (!message) {
      return
    }

    toast.error({
      meta: { title: decodeURIComponent(message) },
      duration: 3000,
    })
  },
  { immediate: true },
)
</script>

<style lang="scss" scoped>
@import '@/uiKit/assets/fonts.scss';
@import '@/uiKit/assets/spacings.scss';

.login__form__input {
  padding-bottom: $spacing-5;
}

.login__form__input--forgot__password {
  @include typographyRegularBody100;
  text-align: right;
  margin-bottom: -$spacing-5;
  color: var(--color-background-primary);
  cursor: pointer;
  float: right;

  &:hover {
    color: var(--color-background-primary-hovered);
  }

  &:active,
  &:focus {
    color: var(--color-background-primary-pressed);
  }
}

.login__form__remember {
  width: 100%;
  @include row;
  justify-content: flex-start;
  margin-bottom: $spacing-5;
}

.login__form__button {
  margin-bottom: $spacing-4;
  width: 100%;
  @include fontRegularBody300;

  &:last-child {
    margin-bottom: 0;
  }
}

.separator {
  margin-bottom: $spacing-5;
}
</style>
